import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  BdVirtualClinicDismissionCause,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  BD_VIRTUAL_CLINIC_DISMISSION_CAUSE_DISPLAY_NAMES,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type { FilterConditionPlugin } from '../filter';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';

export const BD_DISMISSION_CAUSE_CONDITION_ID = 'dismissalReason';
export const FilterConditionDismissionCausePluginType: FilterConditionPluginSpecification<
  BdVirtualClinicDismissionCause[],
  FilterConditionDismissionCauseDto,
  BdDismissionCauseCondition
> = {
  type: 'dismissionCauses',
  category: 'case',
  displayName: { de: 'Entlassgrund' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionDismissionCauseDto,
  ): FilterConditionPluginImpl<BdVirtualClinicDismissionCause[]> {
    return new FilterConditionDismissionCausePlugin(
        obj.dismissionCauses as BdVirtualClinicDismissionCause[],
      );
  },
  fromCondition(
    jsonObj: BdDismissionCauseCondition,
  ): FilterConditionPluginImpl<BdVirtualClinicDismissionCause[]> {
    return new FilterConditionDismissionCausePlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<BdVirtualClinicDismissionCause[]> {
    return new FilterConditionDismissionCausePlugin([]);
  },
};

export class FilterConditionDismissionCausePlugin extends FilterConditionPluginImpl<
  BdVirtualClinicDismissionCause[],
  FilterConditionDismissionCauseDto
> {
  static override Specification: FilterConditionPluginSpecification<
    BdVirtualClinicDismissionCause[],
    FilterConditionDismissionCauseDto,
    BdDismissionCauseCondition
  > = FilterConditionDismissionCausePluginType;

  constructor(private dismissionCauses: BdVirtualClinicDismissionCause[]) {
    super(FilterConditionDismissionCausePluginType.type);
  }

  clone(): FilterConditionPlugin<BdVirtualClinicDismissionCause> {
    return new FilterConditionDismissionCausePlugin([...this.dismissionCauses]);
  }

  override equals(other: FilterConditionDismissionCausePlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.dismissionCauses, other.dismissionCauses)
    );
  }

  override validationToText(): string {
    if (this.dismissionCauses.length === 0) {
      return 'Mindestens einen Entlassgrund auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdDismissionCauseCondition(this.dismissionCauses, []);
  }

  toJSON(): FilterConditionDismissionCauseDto | null {
    return {
      type: this.type,
      dismissionCauses: this.dismissionCauses,
    };
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.dismissionCauses.map((cause) => {
      const displayName =
        BD_VIRTUAL_CLINIC_DISMISSION_CAUSE_DISPLAY_NAMES[cause];
      return displayName ? `${cause} - ${displayName}` : cause;
    });
    return { title, items };
  }

  toText(): string | null {
    return this.dismissionCauses.length === 1
      ? 'einen Entlassgrund'
      : `${this.dismissionCauses.length} Entlassgründe`;
  }

  toValue(): BdVirtualClinicDismissionCause[] {
    return this.dismissionCauses;
  }
}

export class BdDismissionCauseCondition extends BdContentCondition<BdVirtualClinicDismissionCause> {
  constructor(
    includes: BdVirtualClinicDismissionCause[],
    excludes: BdVirtualClinicDismissionCause[],
  ) {
    super(BD_DISMISSION_CAUSE_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionDismissionCauseDto {
  type: string;
  dismissionCauses: string[];
}
